import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// شمارش و ایندکس‌گذاری اسلات‌ها بر اساس [] در متن
function parseBlanksFromText(text = "") {
  if (!text) return { html: "", slots: [] };
  let idx = 0;
  // هر [] را به تگ placeholder تبدیل می‌کنیم تا راحت‌تر رندر کنیم
  const html = text.replace(/\[\s*\]/g, () => {
    idx += 1;
    return `<span data-slot="${idx}" class="wo-slot"></span>`;
  });
  const slots = Array.from({ length: idx }, (_, i) => String(i + 1));
  return { html, slots };
}

function sanitizeHtml(html = "") {
  return html;
}

export default function SentenceReconstructionRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const textRaw = item?.body?.text?.fa || item?.body?.text?.en || "";
  const tokens = Array.isArray(item?.body?.tokens) ? item.body.tokens : [];
  const answersModel = Array.isArray(item?.body?.answers) ? item.body.answers : [];

  // اسلات‌ها را از متن استخراج کن
  const { html, slots } = useMemo(() => parseBlanksFromText(textRaw), [textRaw]);

  // state: چه توکنی روی کدام اسلات نشسته
  const [slotFill, setSlotFill] = useState(() => {
    const init = {};
    if (Array.isArray(initialAnswers) && initialAnswers.length > 0) {
      slots.forEach((slotId, i) => {
        init[slotId] = initialAnswers[i] || "";
      });
    } else {
      slots.forEach((slotId) => (init[slotId] = ""));
    }
    return init;
  });

  // لیست توکن‌های باقی‌مانده (آن‌هایی که در هیچ اسلاتی نیستند)
  const remainingTokens = useMemo(() => {
    const freq = {};
    Object.values(slotFill).forEach((tk) => {
      if (!tk) return;
      freq[tk] = (freq[tk] || 0) + 1;
    });

    const allFreq = tokens.reduce((m, x) => ((m[x] = (m[x] || 0) + 1), m), {});
    const left = [];
    Object.entries(allFreq).forEach(([tk, total]) => {
      const usedCount = freq[tk] || 0;
      const remain = total - usedCount;
      for (let i = 0; i < remain; i++) left.push(tk);
    });
    return left;
  }, [tokens, slotFill]);

  // هر تغییری به والد ریپورت شود
  useEffect(() => {
    const arr = slots.map((slotId) => slotFill[slotId] || "");
    onAnswerChange?.(arr);
  }, [slotFill, slots, onAnswerChange]);

  // هندل کلیک روی توکن‌ها: پر کردن نزدیک‌ترین اسلات خالی
  const placeNext = (token) => {
    const empty = slots.find((id) => !slotFill[id]);
    if (!empty) return;
    setSlotFill((prev) => ({ ...prev, [empty]: token }));
  };

  // قرار دادن مستقیم یک توکن روی اسلات مشخص (براساس drag and drop)
  const placeInSlot = (slotId, token) => {
    setSlotFill((prev) => ({ ...prev, [slotId]: token }));
  };

  // پاک‌کردن اسلات
  const clearSlot = (slotId) => {
    setSlotFill((prev) => ({ ...prev, [slotId]: "" }));
  };

  // --- Drag & Drop Handlers ---
  const handleDragStart = (e, token) => {
    e.dataTransfer.setData("text/plain", token);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropOnSlot = (e, slotId) => {
    e.preventDefault();
    const token = e.dataTransfer.getData("text/plain");
    if (token) {
      placeInSlot(slotId, token);
    }
  };

  // رندر متن با جایگاه‌های اسلات
  const TextWithSlots = useMemo(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizeHtml(html), "text/html");

    const traverse = (node, key) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();

        if (tag === "span" && node.getAttribute("data-slot")) {
          const slotId = node.getAttribute("data-slot");
          const filled = slotFill[slotId] || "";
          return (
            <button
              key={key}
              type="button"
              onClick={() => (filled ? clearSlot(slotId) : undefined)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDropOnSlot(e, slotId)}
              className={
                "inline-flex items-center justify-center min-w-[64px] px-2 py-1 mx-1 rounded-md border transition-all cursor-pointer " +
                (filled
                  ? "bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
                  : "bg-gray-50 border-dashed border-gray-400 text-gray-500 hover:border-purple-400 hover:bg-purple-50/50")
              }
              aria-label={`slot-${slotId}`}
              title={filled ? t("preview.remove_token") : t("preview.empty_slot")}
            >
              {filled || "____"}
            </button>
          );
        }

        const props = { key };
        for (const attr of node.attributes) {
          const name = attr.name;
          const value = attr.value;
          if (name === "class") props.className = value;
          else if (name === "style")
            props.style = value
              .split(";")
              .filter(Boolean)
              .reduce((acc, decl) => {
                const [p, v] = decl.split(":");
                if (!p || !v) return acc;
                const camel = p.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                acc[camel] = v.trim();
                return acc;
              }, {});
          else props[name] = value;
        }

        const children = [];
        let i = 0;
        for (let child = node.firstChild; child; child = child.nextSibling) {
          children.push(traverse(child, `${key}-${i++}`));
        }
        return React.createElement(tag, props, ...children);
      }
      return null;
    };

    const children = [];
    let i = 0;
    for (let child = doc.body.firstChild; child; child = child.nextSibling) {
      children.push(traverse(child, `root-${i++}`));
    }
    return <>{children}</>;
  }, [html, slotFill, t]);

  if (!textRaw || slots.length === 0) {
    return (
      <div className="text-sm text-red-500">
        {t("preview.word_ordering_text_missing")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* متن با اسلات‌ها */}
      <div className="prose prose-sm max-w-none leading-7">
        <div dir={isRtl ? "rtl" : "ltr"}>{TextWithSlots}</div>
      </div>

      {/* بانک کلمات */}
      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
        {remainingTokens.length === 0 ? (
          <div className="text-xs text-gray-500">{t("preview.no_more_tokens")}</div>
        ) : (
          remainingTokens.map((tk, i) => (
            <button
              key={`${tk}-${i}`}
              type="button"
              draggable
              onDragStart={(e) => handleDragStart(e, tk)}
              onClick={() => placeNext(tk)}
              className="px-3 py-1.5 rounded-md border bg-white hover:bg-purple-50 hover:border-purple-300 text-sm cursor-grab active:cursor-grabbing shadow-sm select-none transition-all"
              title={t("preview.add_to_next_slot")}
            >
              {tk}
            </button>
          ))
        )}
      </div>

      {/* وضعیت */}
      <div className="text-xs text-gray-600">
        {t("preview.slots_filled", {
          filled: Object.values(slotFill).filter(Boolean).length,
          total: slots.length,
        })}
      </div>
    </div>
  );
}